import { PostHogProvider, usePostHog } from '@posthog/react';
import posthog from 'posthog-js';
import PropTypes from 'prop-types';
import React from 'react';

const POSTHOG_API_KEY = process.env.REACT_APP_POSTHOG_API_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST;
export const isPostHogEnabled = Boolean(POSTHOG_API_KEY && POSTHOG_HOST);

const posthogOptions = {
  api_host: POSTHOG_HOST,
  defaults: '2026-05-30',
  session_recording: {
    sampleRate: 0.2,
  },
  loaded: posthog => {
    posthog.register({
      app_environment: process.env.NODE_ENV,
    });
  },
};

export const AppPostHogProvider = ({ children }) => {
  if (!isPostHogEnabled) return children;

  return (
    <PostHogProvider apiKey={POSTHOG_API_KEY} options={posthogOptions}>
      {children}
    </PostHogProvider>
  );
};

AppPostHogProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const POSTHOG_EVENTS = {
  COMPLIANCE_WATCH_INTERACTION: 'Compliance Watch interaction',
};

export const resetPostHogUser = () => {
  if (!isPostHogEnabled || !posthog?.reset) return;

  posthog.reset();
};

export const useIdentifyPostHogUser = (userId, email, companyId) => {
  const posthogClient = usePostHog();
  const identifiedUserRef = React.useRef();
  const groupedCompanyIdRef = React.useRef();

  React.useEffect(() => {
    if (!userId) {
      identifiedUserRef.current = undefined;
      groupedCompanyIdRef.current = undefined;
      return;
    }

    if (!isPostHogEnabled || !posthogClient) return;
    const identifiedUser = identifiedUserRef.current;
    if (
      posthogClient.identify &&
      (identifiedUser?.userId !== userId || identifiedUser?.email !== email)
    ) {
      posthogClient.identify(userId, email ? { email } : undefined);
      identifiedUserRef.current = { email, userId };
    }

    if (companyId && posthogClient?.group && groupedCompanyIdRef.current !== companyId) {
      posthogClient.group('company', companyId);
      groupedCompanyIdRef.current = companyId;
    }
  }, [companyId, email, posthogClient, userId]);
};

export const useAnalytics = () => {
  const posthogClient = usePostHog();

  const capture = React.useCallback(
    (eventName, properties = {}) => {
      if (!isPostHogEnabled || !posthogClient?.capture) return;

      posthogClient.capture(eventName, properties);
    },
    [posthogClient],
  );

  return { capture, isEnabled: isPostHogEnabled };
};
